import { useState, useEffect, useMemo, useCallback,useRef } from 'react'
import { useRecoilState } from 'recoil'
import { alarmReviewSelectedTimeState,selectBedInfoState,isFromSearchState } from '../../plugins/recoil'
import moment, { duration } from 'moment'

import {ReactComponent as IconLeftArrowTripple} from '../../assets/icons/ic-arrow-left-tripple.svg'
import {ReactComponent as IconRightArrowTripple} from '../../assets/icons/ic-arrow-right-tripple.svg'
import {ReactComponent as IconLeftArrow} from '../../assets/icons/ic-timeline-left.svg'
import {ReactComponent as IconRightArrow} from '../../assets/icons/ic-timeline-right.svg'
import {ReactComponent as IconSetting} from '../../assets/icons/ic-setting.svg';

const Timeline = ({ patientHistories, alarmList, startTime, endTime, handleChangeTime, handleTimeSettings, canSearchMoves}) => {
    const [selectedTime] = useRecoilState(alarmReviewSelectedTimeState);
    const [selectBedInfo, ] = useRecoilState(selectBedInfoState);     
    const [isFromSearch] = useRecoilState(isFromSearchState);     
    
    const eventColor = (eventPrioirity) => {
        switch (eventPrioirity) {
            case 0:
                return '#FFF500'; // Yellow
            case 1:
                return '#FFF500'; // Yellow
            case 2:
                return '#FF002E'; // Red
            default:
                return '#FFFFFF'; // Default White
        }
    }

    // 하단 타임 슬롯 변경. (3시간, 6시간 : 15분 간격) (12시간 24시간 : 1시간 간격)
    const generateTimeSlots = (start, end) => {
        const timeSlots = []
        const startMoment = moment(start)
        const endMoment = moment(end)
        
        let current = startMoment.clone()
        
        while (current.isBefore(endMoment)) {
            timeSlots.push(current.clone())
            current.add(1, 'hour');
        }
        
        return timeSlots
    }

    const getGlobalSections = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        
        // 전체 구분선을 시간순으로 정렬
        const allDividers = timelinePatientRenderInfos
            .filter(info => info.type === 'divider')
            .sort((a, b) => moment(a.eventTime).diff(moment(b.eventTime)))
        
        const globalSections = []
        let sectionStart = timelineStart.clone()
        
        // 첫 번째 구분선 이전 구간 (타임라인 시작 ~ 첫 구분선)
        if (allDividers.length > 0) {
            const firstDividerTime = moment(allDividers[0].eventTime)
            globalSections.push({
                startTime: sectionStart.clone(),
                endTime: firstDividerTime.clone(),
                sectionIndex: 0
            })
            sectionStart = firstDividerTime.clone()
        }
        
        // 구분선 사이의 구간들
        allDividers.forEach((divider, index) => {
            if (index < allDividers.length - 1) {
                const nextDividerTime = moment(allDividers[index + 1].eventTime)
                globalSections.push({
                    startTime: sectionStart.clone(),
                    endTime: nextDividerTime.clone(),
                    sectionIndex: index + 1
                })
                sectionStart = nextDividerTime.clone()
            }
        })
        
        // 마지막 구분선 이후 구간 (마지막 구분선 ~ 타임라인 끝)
        if (allDividers.length > 0) {
            globalSections.push({
                startTime: sectionStart.clone(),
                endTime: timelineEnd.clone(),
                sectionIndex: allDividers.length
            })
        } else {
            // 구분선이 없는 경우 전체가 하나의 구간
            globalSections.push({
                startTime: timelineStart.clone(),
                endTime: timelineEnd.clone(),
                sectionIndex: 0
            })
        }
        
        return globalSections
    }

    const getPatientHistoryRenderInfo = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')

        const allEvents = patientHistories
            .map(history => ({
                ...history,
                timestamp: moment(history.messageTimestamp)
            }))
            .sort((a, b) => a.timestamp.diff(b.timestamp))
        
        const renderInfos = []
        
        allEvents.forEach((event) => {
            const eventTime = event.timestamp
            
            if (eventTime.isBetween(timelineStart, timelineEnd, null, '[]')) {
                const offsetSeconds = eventTime.diff(timelineStart, 'seconds')
                const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                
                renderInfos.push({
                    type: 'divider',
                    leftPercent,
                    eventTime: event.messageTimestamp,
                    patientName: event.patientName,
                    devicePatientId: event.devicePatientId
                })
            }
        });
        
        return renderInfos
    }

    const getTimelineEventRenderInfo = () => {
        const timelineStart = moment(startTime)
        const timelineEnd = moment(endTime)
        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')
        
        return alarmList.map(event => {
            const eventStart = moment(event.startTime)
            const eventEnd = moment(event.endTime)
            
            if (eventStart.isBefore(timelineEnd) && eventEnd.isAfter(timelineStart)) {
                const renderStart = moment.max(eventStart, timelineStart)
                const renderEnd = moment.min(eventEnd, timelineEnd)
                
                const offsetSeconds = renderStart.diff(timelineStart, 'seconds')
                const durationSeconds = renderEnd.diff(renderStart, 'seconds')
                
                const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                const widthPercent = (durationSeconds / totalDurationSeconds) * 100
                
                return {
                    ...event,
                    leftPercent,
                    widthPercent,
                    color: eventColor(event.eventPriority)
                }
            }
            return null
        }).filter(Boolean)
    }

    const calculateTextLeftPosition = (text, isFirstSlot = false) => {
        const charWidth = 5.8; 
        const textWidth = text.length * charWidth;
        
        if (isFirstSlot) {
            return 2; 
        }
        
        return -(textWidth / 2);
    };

    const timeSlots = useMemo(() => {
        return generateTimeSlots(startTime, endTime)
    }, [startTime, endTime])

    const slotWidth = useMemo(() => {
        return timeSlots.length > 0 ? `calc(100% / ${timeSlots.length})` : '100%'
    }, [timeSlots.length])
    
    const timelineEventRenderInfos = useMemo(() => {
        return getTimelineEventRenderInfo()
    }, [alarmList, startTime, endTime])
    
    const timelinePatientRenderInfos = useMemo(() => {
        return getPatientHistoryRenderInfo()
    }, [patientHistories, startTime, endTime])
    
    const globalSections = useMemo(() => {
        return getGlobalSections()
    }, [timelinePatientRenderInfos, startTime, endTime])
            
    const nextDisabled = moment().isAfter(moment(endTime).add(24, 'hours'))    

    return (
        <div className="flex h-full px-5 pt-3 items-start relative timeline-container">
            {selectedTime && (
                (() => {
                    const selectedMoment = moment(selectedTime)
                    const timelineStart = moment(startTime)
                    const timelineEnd = moment(endTime)
                    
                    // selectedTime이 타임라인 범위 내에 있는지 확인
                    if (selectedMoment.isBetween(timelineStart, timelineEnd, null, '[]')) {
                        const totalDurationSeconds = timelineEnd.diff(timelineStart, 'seconds')
                        const offsetSeconds = selectedMoment.diff(timelineStart, 'seconds')
                        const leftPercent = (offsetSeconds / totalDurationSeconds) * 100
                        
                        // 버튼 영역 제외하고 타임라인 영역에서의 위치 계산
                        const leftButtonWidth = 65 // 42px + 3px margin + 20px padding
                        const rightButtonWidth = 65 // 42px + 3px margin
                        
                        return (
                            <div 
                                className="absolute top-0 w-[1px] border-dashed z-40"
                                style={{ 
                                    left: `calc(${leftButtonWidth}px + (100% - ${leftButtonWidth + rightButtonWidth}px) * ${leftPercent / 100})`,
                                    height: '100%',
                                    borderLeft: '1px dashed #CECECE'
                                }}
                            />
                        )
                    }
                    return null
                })()
            )}
            <div className='flex'>
                <div 
                    className="cursor-pointer flex items-center justify-center h-[26px] bg-[#2C2C2C] mr-[2px]"
                    style={{ borderTopLeftRadius: 888, borderBottomLeftRadius: 888, minWidth: 36, maxWidth: 36, opacity: isFromSearch ? (canSearchMoves.trendPrev ? 1 : 0.3) : 1 }}
                    onClick={isFromSearch ? (canSearchMoves.trendPrev ? () => { handleChangeTime('prevDuration') } : null) : (() => { handleChangeTime('prevDuration') })}>
                    <IconLeftArrow />
                </div>
            </div>
            
            <div className="flex-1 h-[26px] relative">
                <div className="flex w-full">
                    {timeSlots.map((timeMoment, index) => {
                        const currentDate = timeMoment.local().format('YYYY-MM-DD')
                        const prevDate = index > 0 ? timeSlots[index - 1].local().format('YYYY-MM-DD') : currentDate
                        const isDateChanged = currentDate !== prevDate
                    
                        const slotStart = timeMoment
                        // ✅ 올바른 slotEnd 계산
                        const slotEnd = index < timeSlots.length - 1 
                            ? timeSlots[index + 1]  // 다음 슬롯의 시작 시간 (15분 또는 1시간 후)
                            : moment(endTime)       // 마지막 슬롯이면 전체 끝 시간

                        // ✅ 실제 슬롯 길이 계산
                        const slotDurationSeconds = slotEnd.diff(slotStart, 'seconds')

                        const overlappingSections = globalSections.filter(section => 
                            section.startTime.isBefore(slotEnd) && section.endTime.isAfter(slotStart)
                        )
                        
                        // 빗금 구간 생성 - 더 간단한 체크
                        // const hatchSections = []

                        // overlappingSections.forEach((section, sectionIndex) => {
                        //     const overlapStart = moment.max(section.startTime, slotStart)
                        //     const overlapEnd = moment.min(section.endTime, slotEnd)                            
                        //     const offsetSeconds = overlapStart.diff(slotStart, 'seconds')
                        //     const durationSeconds = overlapEnd.diff(overlapStart, 'seconds')                            
                        //     const leftPercent = (offsetSeconds / slotDurationSeconds) * 100
                        //     const widthPercent = (durationSeconds / slotDurationSeconds) * 100
                            
                        //     // 새로운 빗금 조건 (데이터 기반 구간별 체크)                            
                        //     const hasCurrentPatient = (() => {
                        //         // 1. patientHistories가 아예 없는 경우는 빗금 X
                        //         if (!patientHistories || patientHistories.length === 0) {
                        //             return true // 빗금 없음
                        //         }
                                
                        //         // 2. selectBoxTimeInfo가 있는 경우
                        //         if (selectBoxTimeInfo) {
                        //             const waveTime = moment(selectBoxTimeInfo)
                        //             const timelineStart = moment(startTime)
                                    
                        //             // 환자별로 그룹화
                        //             const patientGroups = {}
                        //             patientHistories.forEach(history => {
                        //                 if (!patientGroups[history.patientId]) {
                        //                     patientGroups[history.patientId] = []
                        //                 }
                        //                 patientGroups[history.patientId].push(history)
                        //             })
                                    
                        //             // 각 환자의 기간을 체크하여 waveStateTime이 포함되는 환자 찾기
                        //             for (const patientId of Object.keys(patientGroups)) {
                        //                 const histories = patientGroups[patientId].sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)))
                                        
                        //                 let admittedHistory = null
                                        
                        //                 for (const history of histories) {
                        //                     if (history.action === 'ADMITTED') {
                        //                         admittedHistory = history
                        //                     } else if (history.action === 'UNASSIGNED') {
                        //                         if (admittedHistory) {
                        //                             // 입원-퇴원 쌍
                        //                             const patientStart = moment(admittedHistory.createdAt)
                        //                             const patientEnd = moment(history.createdAt)
                                                    
                        //                             // waveTime이 이 환자 기간에 포함되는지 체크
                        //                             if (waveTime.isBetween(patientStart, patientEnd, null, '[]')) {
                        //                                 // 그리고 이 환자 기간이 현재 구간과 겹치는지 체크
                        //                                 if (patientStart.isBefore(overlapEnd) && patientEnd.isAfter(overlapStart)) {
                        //                                     //console.log(`Wave is in patient ${patientId} period - removing hatch for entire period`)
                        //                                     return true // 빗금 없음
                        //                                 }
                        //                             }
                        //                             admittedHistory = null
                        //                         } else {
                        //                             // 퇴원만 있는 경우 (타임라인 시작부터 퇴원까지)
                        //                             const patientStart = timelineStart
                        //                             const patientEnd = moment(history.createdAt)
                                                    
                        //                             // waveTime이 이 기간에 포함되는지 체크
                        //                             if (waveTime.isBetween(patientStart, patientEnd, null, '[]')) {
                        //                                 // 그리고 이 기간이 현재 구간과 겹치는지 체크
                        //                                 if (patientStart.isBefore(overlapEnd) && patientEnd.isAfter(overlapStart)) {
                        //                                     //console.log(`Wave is in discharge-only period - removing hatch for entire period`)
                        //                                     return true // 빗금 없음
                        //                                 }
                        //                             }
                        //                         }
                        //                     }
                        //                 }
                                        
                        //                 // 현재 입원 중인 환자 처리
                        //                 if (admittedHistory) {
                        //                     const patientStart = moment(admittedHistory.createdAt)
                        //                     const patientEnd = moment(endTime)
                                            
                        //                     // waveTime이 이 환자 기간에 포함되는지 체크
                        //                     if (waveTime.isBetween(patientStart, patientEnd, null, '[]')) {
                        //                         // 그리고 이 환자 기간이 현재 구간과 겹치는지 체크
                        //                         if (patientStart.isBefore(overlapEnd) && patientEnd.isAfter(overlapStart)) {                                                    
                        //                             return true // 빗금 없음
                        //                         }
                        //                     }
                        //                 }
                        //             }
                        //         }

                        //         //마지막 조건 추가
                        //         if (patientLatestHistory && patientLatestHistory.action === 'UNASSIGNED' && selectBoxTimeInfo) {
                        //             // 같은 patientId의 UNASSIGNED 찾기
                        //             const matchingUnassigned = patientHistories.find(h => 
                        //                 h.action === 'UNASSIGNED' && 
                        //                 h.patientId === patientLatestHistory.patientId
                        //             );
                                    
                        //             if (matchingUnassigned) {
                        //                 const selectBoxTime = moment(selectBoxTimeInfo);
                        //                 const timelineStartTime = moment(startTime);
                        //                 const unassignedTime = moment(matchingUnassigned.createdAt);
                        //                 const timelineEndTime = moment(endTime);
                                        
                        //                 // 1. selectBoxTimeInfo가 startTime ~ UNASSIGNED 시간에 겹치는 경우
                        //                 if (selectBoxTime.isBetween(timelineStartTime, unassignedTime, null, '[]')) {
                        //                     // 현재 구간이 startTime ~ UNASSIGNED 구간과 겹치는지 체크
                        //                     if (overlapStart.isBefore(unassignedTime) && overlapEnd.isAfter(timelineStartTime)) {
                        //                         return true; // 빗금 없음
                        //                     }
                        //                 }
                                        
                        //                 // 2. selectBoxTimeInfo가 UNASSIGNED ~ endTime 시간에 겹치는 경우
                        //                 if (selectBoxTime.isBetween(unassignedTime, timelineEndTime, null, '[]')) {
                        //                     // 현재 구간이 UNASSIGNED ~ endTime 구간과 겹치는지 체크
                        //                     if (overlapStart.isBefore(timelineEndTime) && overlapEnd.isAfter(unassignedTime)) {
                        //                         return true; // 빗금 없음
                        //                     }
                        //                 }
                        //             }
                        //         }

                        //         //Unassigned 기록 후 다음에 admitted 기록이 있는 경우의 구간 (Not Admitted 구간)
                        //         //해당 구간에 selectBoxTimeInfo이 있는 경우에는 빗금 처리를 안함. 다만 다른 구간에 있을 경우에는 빗금처리
                        //         const unassignedHistories = patientHistories
                        //             .filter(history => history.action === 'UNASSIGNED')
                        //             .sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)))
                        //         for (const unassignedHistory of unassignedHistories) {
                        //             const unassignedTime = moment(unassignedHistory.createdAt)
                        //             const nextAdmittedHistory = patientHistories
                        //                 .filter(history => history.action === 'ADMITTED' && moment(history.createdAt).isAfter(unassignedTime))
                        //                 .sort((a, b) => moment(a.createdAt).diff(moment(b.createdAt)))[0]
                        //             const nextAdmittedTime = nextAdmittedHistory ? moment(nextAdmittedHistory.createdAt) : moment(endTime)
                        //             // selectBoxTimeInfo가 이 Not Admitted 구간에 포함되는지 체크
                        //             if (selectBoxTimeInfo) {
                        //                 const selectBoxTime = moment(selectBoxTimeInfo)
                        //                 if (selectBoxTime.isBetween(unassignedTime, nextAdmittedTime, null, '[]')) {
                        //                     // 그리고 이 구간이 현재 겹치는지 체크
                        //                     if (unassignedTime.isBefore(overlapEnd) && nextAdmittedTime.isAfter(overlapStart)) {
                        //                         return true // 빗금 없음
                        //                     }
                        //                 }
                        //             }
                        //         }                                
                                
                        //         // 3. 그 외의 경우는 빗금 표시
                        //         return false // 빗금 있음
                        //     })()

                        //     //기존 로직
                        //     if(!isFromSearch){
                        //         hatchSections.push({
                        //             left: leftPercent,
                        //             width: widthPercent,
                        //             shouldShowHatch: !hasCurrentPatient
                        //         })
                        //     }
                        // })

                        const slotEvents = timelineEventRenderInfos.filter(eventInfo => {
                            const eventStart = moment(eventInfo.startTime)
                            const eventEnd = moment(eventInfo.endTime)
                            return eventStart.isBefore(slotEnd) && eventEnd.isAfter(slotStart)
                        })

                        const slotDividers = timelinePatientRenderInfos.filter(dividerInfo =>
                            dividerInfo.type === 'divider' &&
                            moment(dividerInfo.eventTime).isBetween(slotStart, slotEnd, null, '[]')
                        )

                        return (
                            <div 
                                key={index}
                                className="relative h-[12px] bg-[#2F2F2F] flex items-center"
                                style={{ width: slotWidth }}
                            >
                                {/* {hatchSections.map((section, sectionIndex) => (
                                    <div
                                        key={`hatch-${sectionIndex}`}
                                        className="absolute"
                                        style={{
                                            left: `${section.left}%`,
                                            width: `${section.width}%`,
                                            height: '24px',
                                            top: '0px',
                                            zIndex: 31,
                                            background: section.shouldShowHatch 
                                                ? 'repeating-linear-gradient(135deg, transparent, transparent 2px, #6C6C6C80 2px, #6C6C6C80 4px)'
                                                : 'transparent'
                                        }}
                                    />
                                ))} */}

                                {slotDividers.map((dividerInfo, dividerIndex) => {
                                    const dividerTime = moment(dividerInfo.eventTime)
                                    const offsetSeconds = dividerTime.diff(slotStart, 'seconds')
                                    // ✅ 실제 슬롯 길이 사용
                                    const leftPercent = (offsetSeconds / slotDurationSeconds) * 100

                                    return (
                                        <div
                                            key={`divider-${dividerIndex}`}
                                            className="absolute"
                                            style={{
                                                left: `${leftPercent}%`,
                                                width: '0px',
                                                height: '24px', // 시간 표시 바로 위까지
                                                top: '0px',
                                                zIndex: 31
                                            }}
                                            title={dividerInfo.devicePatientId || '-'}
                                        >
                                            <div
                                                className="absolute"
                                                style={{
                                                    top: '0px',
                                                    left: '-3px',
                                                    width: '0',
                                                    height: '0',
                                                    transform: 'rotate(-90deg)',
                                                    borderBottom: '6px solid #CECECE',
                                                    borderLeft: '6px solid transparent'
                                                }}
                                            />
                                            
                                            <div
                                                className="absolute"
                                                style={{
                                                    top: '0px',
                                                    left: '3px',
                                                    width: '1px',
                                                    height: '100%', // 위아래 삼각형 제외
                                                    backgroundColor: '#CECECE'
                                                }}
                                            />
                                            
                                            <div
                                                className="absolute"
                                                style={{
                                                    bottom: '0px',
                                                    left: '-3px',
                                                    width: '0',
                                                    height: '0',
                                                    transform: 'rotate(90deg)',
                                                    borderTop: '6px solid #CECECE',
                                                    borderLeft: '6px solid transparent'
                                                }}
                                            />
                                        </div>
                                    )
                                })}                               

                                {/* eventPriority 순서가 2,1,나머지 순으로 변경되어 Sort 설정 변경함. */}
                                {slotEvents                                    
                                    .sort((a, b) => {
                                        // 2번 high
                                        if (a.eventPriority === 2 && b.eventPriority !== 2) return -1
                                        if (a.eventPriority !== 2 && b.eventPriority === 2) return 1
                                        
                                        // 1번 medium
                                        if (a.eventPriority === 1 && b.eventPriority !== 1) return -1
                                        if (a.eventPriority !== 1 && b.eventPriority === 1) return 1

                                        // 0번 low
                                        if (a.eventPriority === 0 && b.eventPriority !== 0) return -1
                                        if (a.eventPriority !== 0 && b.eventPriority === 0) return 1   
                                        
                                        // 나머지는 기본 오름차순
                                        return a.eventPriority - b.eventPriority
                                    })
                                    .map((eventInfo, eventIndex) => {
                                        const eventStart = moment(eventInfo.startTime)
                                        const eventEnd = moment(eventInfo.endTime)
                                        
                                        // 슬롯 내에서의 상대적 위치 계산
                                        const renderStart = moment.max(eventStart, slotStart)
                                        const renderEnd = moment.min(eventEnd, slotEnd)

                                        const offsetSeconds = renderStart.diff(slotStart, 'seconds')
                                        const durationSeconds = renderEnd.diff(renderStart, 'seconds')

                                        // ✅ 실제 슬롯 길이 사용
                                        const leftPercent = (offsetSeconds / slotDurationSeconds) * 100
                                        const widthPercent = (durationSeconds / slotDurationSeconds) * 100

                                        return (
                                            <div
                                                key={eventIndex}
                                                className="absolute h-full"
                                                style={{
                                                    left: `${leftPercent}%`,
                                                    width: `${widthPercent}%`,
                                                    backgroundColor: eventInfo.color,
                                                    zIndex: eventInfo.eventPriority === 2 ? 32 : (eventInfo.eventPriority === 1 || eventInfo.eventPriority === 0)  ? 31 : 30
                                                }}
                                            />
                                        )
                                })}
                                <div className="absolute w-[0px] h-[12px] top-[12px]" style={{ border: '1px solid #2F2F2F' }} />                                
                                <div className="absolute w-[0px] h-[6px] top-[12px] left-[25%]" style={{ border: '1px solid #2F2F2F' }} />
                                <div className="absolute w-[0px] h-[6px] top-[12px] left-[50%]" style={{ border: '1px solid #2F2F2F' }} />
                                <div className="absolute w-[0px] h-[6px] top-[12px] left-[75%]" style={{ border: '1px solid #2F2F2F' }} />                                
                                
                                {
                                    index === timeSlots.length - 1 &&
                                    <div className="absolute w-[0px] h-[12px] top-[12px] right-0" style={{ border: '1px solid #2F2F2F' }} />
                                }
                                <div className="absolute text-[#DEDEDE] font-light top-[24px]" style={{ fontSize: 12, left: calculateTextLeftPosition(timeMoment.format('HH:mm'), index === 0) }}>
                                    {timeMoment.local().format('HH:mm')}
                                </div>
                                {isDateChanged && (
                                    <div className="absolute text-[#DEDEDE] font-light top-[40px]" style={{ fontSize: 12, left: calculateTextLeftPosition(`[${timeMoment.format('YY/MM/DD')}]`, index === 0) }}>
                                        {`[${timeMoment.format('YY/MM/DD')}]`}
                                    </div>
                                )}
                                {
                                    index === timeSlots.length - 1 &&
                                    <div className="absolute top-[24px] right-0 text-[#DEDEDE] font-light" style={{ fontSize: 12 }}>
                                        { moment(endTime).local().format('HH:mm') }
                                    </div>
                                }
                                {index === timeSlots.length - 1 && moment(endTime).local().format('YYYY-MM-DD') !== currentDate && (
                                    <div className="absolute top-[40px] right-0 text-[#DEDEDE] font-light" style={{ fontSize: 12 }}>
                                        {`[${moment(endTime).local().format('YY/MM/DD')}]`}
                                    </div>
                                )}
                            </div>
                        )                        
                    })}
                </div>                            
            </div>
            <div className='flex'> 
                <div 
                    className="cursor-pointer flex items-center justify-center h-[26px] bg-[#2C2C2C] ml-[2px]"
                    style={{ borderTopRightRadius: 888, borderBottomRightRadius: 888, minWidth: 36, maxWidth: 36,opacity: isFromSearch ? (canSearchMoves.trendNext ? 1 : 0.3) : (!nextDisabled ? 0.3 : 1), cursor: isFromSearch ? (canSearchMoves.trendNext ? 'pointer' : 'default') : (!nextDisabled ? 'default' : 'pointer') }}
                    onClick={isFromSearch ? (canSearchMoves.trendNext ? () => { handleChangeTime('nextDuration') } : null) : (() => { if (nextDisabled) handleChangeTime('nextDuration') })}>
                    <IconRightArrow />
                </div>
            </div>
            <div className='flex ml-[12px] cursor-pointer' onClick={handleTimeSettings}>
                <IconSetting />
            </div>
        </div>
    )
}

export default Timeline